import { useState } from "react";
import { useDocumentHead } from "@/hooks/useDocumentHead.ts";

function AboutNewsletterForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const res = await fetch("/api/pg/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "shop-about" }),
      });
      const data = await res.json();
      setStatus(data.success ? "success" : "error");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return <p className="font-semibold text-emerald-600">Thanks for subscribing! Check your inbox for a welcome email.</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-2 max-w-md">
      <input
        type="email"
        required
        placeholder="Enter your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="min-w-0 flex-1 border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-heading"
      />
      <button
        type="submit"
        disabled={status === "loading"}
        className="whitespace-nowrap bg-heading px-5 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        {status === "loading" ? "..." : "Subscribe"}
      </button>
    </form>
  );
}

export function AboutPage() {
  useDocumentHead({
    title: "About Us",
    description: "Learn about The Secret Trends — we curate the best trending products with expert reviews and exclusive deals to help you discover what's worth buying.",
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-16">
      <h1 className="text-3xl font-bold text-gray-900">About Us</h1>

      <div className="mt-8 space-y-6 text-gray-600 leading-relaxed">
        <p>
          Since 2016, The Secret Trends has been dedicated to bringing you the best
          products on the market. Our team researches, tests, and curates 2-4
          exceptional products every month from brands that share our values of
          quality and innovation.
        </p>

        <p>
          We believe shopping should be an experience of discovery. That's why we
          partner directly with brands to negotiate exclusive discounts and special
          offers you won't find anywhere else.
        </p>

        <p>
          We focus on supporting small businesses and entrepreneurs who create
          products that truly make a difference in people's lives. Every product in
          our shop has been handpicked because we believe in it.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 pt-4">Our Mission</h2>
        <p>
          To connect you with innovative products from passionate creators, making
          it easy to discover items that improve your everyday life. We handle the
          research so you don't have to.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 pt-4">Stay Connected</h2>
        <p>
          Subscribe to our free newsletter to be the first to know about new product
          discoveries and exclusive deals. Follow us on social media for daily
          inspiration and behind-the-scenes looks at the products we're testing.
        </p>
        <AboutNewsletterForm />
      </div>
    </div>
  );
}
